import { useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { useToast } from "@songtradr-one/components-frontend";
import { appModulesList } from "@songtradr-one/shared-common";
import { useAppContext } from "@songtradr-one/shared-frontend";
import { ControlCenter } from "../../../shared/components/control-center/ControlCenter";
import { Props, Wrapper } from "./ControlCenterDropdown.Parts";
import { useFloating } from "./use-floating/UseFloating";
import { useInteractions } from "./use-interactions/UseInteractions";

export const ControlCenterDropdown = ({
  anchor,
  open,
  onOpenChange,
}: Props) => {
  const { toastContainerRef } = useToast();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hasAnyPermission, hasAllPermissions } = useAppContext();

  const listRef = useRef<(HTMLElement | null)[]>([]);
  const labelsRef = useRef<string[]>([]);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const drawerOpen = Boolean(searchParams.get("drawer"));

  const dashboardModules = appModulesList
    .filter(
      (
        module,
      ): module is typeof module & {
        dashboardOrder: number;
        baseRoute: string;
      } =>
        module.dashboardOrder !== undefined &&
        module.baseRoute !== undefined &&
        (module.match === "AnyPermission"
          ? hasAnyPermission(module.permissions)
          : hasAllPermissions(module.permissions)),
    )
    .sort((a, b) => a.dashboardOrder - b.dashboardOrder);

  const activeModule = dashboardModules.findIndex((module) =>
    location.pathname.startsWith(module.baseRoute),
  );
  const disabledIndices = activeModule !== -1 ? [activeModule] : [];

  labelsRef.current = dashboardModules
    .filter((module) => Boolean(module.label))
    .map((module) => module.label ?? "");

  const floating = useFloating({
    open: open,
    onOpenChange: onOpenChange,
    offset: 0,
  });

  const interactions = useInteractions({
    anchor,
    floatingContext: floating.context,
    toastContainerRef,
    activeIndex,
    setActiveIndex,
    listRef,
    labelsRef,
    disabledIndices,
  });

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={floating.context}
        modal={false}
        closeOnFocusOut={drawerOpen ? false : true}
        initialFocus={undefined}
      >
        <Wrapper
          style={floating.floatingStyles}
          {...interactions.getFloatingProps({
            ref: floating.refs.setFloating,
            role: "menu",
          })}
        >
          <ControlCenter
            arrowKeyNavigation={{
              getItemProps: interactions.getItemProps,
              activeIndex: activeIndex,
              listRef: listRef,
            }}
          />
        </Wrapper>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};
